import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import { FORM_TYPES } from 'src/components/form-builder/CreateQuestionnaire/constants/formTypes';
import { FormikProvider, useFormik } from 'formik';
import * as yup from 'yup';
import FormFields from 'src/components/custom-form/UserCustomFormFields.jsx';
import { organizeFormGroups } from 'src/components/form-builder/CreateQuestionnaire/components/Preview/Preview';
import HeaderTitle from 'src/components/UI/header/HeaderTitle';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const Container = styled('div')({
  boxSizing: 'border-box',
  border: '1px solid #ccc',
  borderRadius: '8px',
  boxShadow: '0 1px 5px rgb(0 74 116 / 15%)',
  maxWidth: '900px',
  padding: '24px',
  marginLeft: 'auto',
  marginRight: 'auto',
  backgroundColor: '#fff'
});

export default function ViewFormResponseModal({ readOnly, selectedResponse }) {
  const t = useAutoTranslation();

  const { form: schema, responses = {}, signature } = selectedResponse;

  const [initialFormikValues, setInitialFormikValues] = useState(
    Object.keys(responses).length ? responses : {}
  );
  const [validationSchema, setValidationSchema] = useState(null);
  const [specificConditon, setSpecificConditon] = useState('');

  if (schema?.config?.schema?.[schema?.config?.startingGroupId]) {
    schema.config.schema[schema.config.startingGroupId].renderCondition = true;
  }

  useEffect(() => {
    if (!schema?.config?.schema) return;
    const yupValidationSchema = {};
    const initialValues = initialFormikValues;
    Object.keys(schema.config.schema).map((schemaId) => {
      let data = schema.config.schema[schemaId];
      (data.condition
        ? data.renderCondition === undefined
          ? true
          : data.renderCondition
        : true) &&
        data.formElements.map((element) => {
          let key = encodeURIComponent(element.label.replaceAll('.', '_'));

          if (element.isRequired) {
            if (
              element.type === FORM_TYPES.MULTIPLE_CHOICE ||
              element.type === FORM_TYPES.LINEAR_SCALE
            ) {
              yupValidationSchema[key] = yup
                .string()
                .required(t('{{label}} is required', { label: element.label }))
                .oneOf(element.options);
            } else if (element.type === FORM_TYPES.SHORT_TEXT) {
              yupValidationSchema[key] = yup
                .array()
                .min(1)
                .of(yup.string().required())
                .required();
            } else if (element.type === FORM_TYPES.CHECKBOX_INPUT) {
              yupValidationSchema[key] = yup
                .array()
                .of(
                  yup.object().shape({
                    checked: yup.boolean()
                  })
                )
                .test({
                  message: `${element.label} is required`,
                  test: (val) => !val.every((val) => val.checked === false)
                });
            } else {
              yupValidationSchema[key] = yup
                .string()
                .required(t('{{label}} is required', { label: element.label }));
            }
          }

          if (element.type === FORM_TYPES.CHECKBOX_INPUT) {
            const values = [];
            if (!initialValues[[key]]) {
              for (const option of element.options) {
                values.push({ label: option, checked: false, value: option });
              }
              if (element.isIdentifier) {
                values.push({ label: 'Other', checked: false, value: '' });
              }
            }
            initialValues[[key]] = initialValues[[key]] || [...values];
          } else if (element.type === FORM_TYPES.SHORT_TEXT) {
            initialValues[[key]] = initialValues[[key]] || [''];
          } else {
            initialValues[[key]] = initialValues[[key]] || '';
          }
        });
    });

    setInitialFormikValues(initialValues);
    setValidationSchema(yup.object(yupValidationSchema));
  }, [specificConditon]);

  const formik = useFormik({
    initialValues: initialFormikValues,
    validationSchema: validationSchema
  });

  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (!schema?.config?.schema) return;
    {
      Object.keys(schema.config.schema).map((schemaId) => {
        let formData = schema.config.schema[schemaId];

        const key = formData.condition?.condition.split('| ')[1];
        const value = formData.condition?.value;
        const formikValue = formik.values[key];

        const previousFormRenderCondition =
          formData.previousConnections
            .map((id) => {
              return schema.config.schema[id]?.renderCondition;
            })
            .indexOf(true) > -1
            ? true
            : false;

        function arrayIncludesAll(required, answer) {
          const checked = [];
          for (const item of answer) {
            if (item.checked) {
              checked.push(item.value);
            }
          }
          return (
            Array.isArray(required) &&
            required.length > 0 &&
            required.every((val) => checked.includes(val))
          );
        }

        let condition;
        if (
          key &&
          value &&
          Array.isArray(value) &&
          Array.isArray(formikValue)
        ) {
          condition =
            arrayIncludesAll(value, formikValue) && previousFormRenderCondition;
        } else {
          if (key)
            condition =
              formik.values[encodeURIComponent(key)] === value &&
              previousFormRenderCondition;
        }

        if (formData.condition) {
          setSpecificConditon(condition === undefined ? true : condition);
          schema.config.schema[schemaId].renderCondition =
            condition === undefined ? true : condition;
        } else {
          setSpecificConditon(false);
        }
      });
    }

    const groups = organizeFormGroups(schema.config);

    setGroups(groups);
  }, [formik.values]);

  useEffect(() => {
    formik.setValues(initialFormikValues);
  }, [initialFormikValues]);

  if (!schema?.config?.schema) return null;

  let signatureFieldLabel = '';
  const hasSignatureField = Object.values(schema.config.schema).some((group) =>
    group.formElements?.some((el) => {
      if (el.type === FORM_TYPES.SIGNATURE) {
        signatureFieldLabel = el.label;
        return true;
      }
      return false;
    })
  );
  const isSigned = Object.values(formik.values).some(
    (v) => typeof v === 'string' && v.startsWith('data:image/')
  );

  return (
    <div>
      {groups &&
        groups.map((group, index) => {
          if (group[0].data.renderCondition) {
            const isLastGroup =
              index ===
              groups.reduce(
                (last, g, i) => (g[0].data.renderCondition ? i : last),
                -1
              );
            return (
              <Container key={index} style={{ marginBlock: 20 }}>
                {group.map((form, index) => {
                  if (form.type === 'form') {
                    return (
                      form &&
                      (form.data.condition
                        ? form.data.renderCondition
                        : true) && (
                        <div key={form.data.id}>
                          <HeaderTitle text={form.data.title} />
                          <Typography gutterBottom>
                            {form.data.description}
                          </Typography>
                          <FormikProvider value={formik}>
                            {form.data.formElements.map((element, index) => {
                              return (
                                <FormFields
                                  element={element}
                                  key={index}
                                  formik={formik}
                                  readOnly={readOnly}
                                  signature={signature}
                                />
                              );
                            })}
                          </FormikProvider>
                        </div>
                      )
                    );
                  } else {
                    return (
                      form &&
                      (form.data.condition
                        ? form.data.renderCondition
                        : true) && (
                        <div key={form.data.id}>
                          <FormikProvider value={formik}>
                            {form.data.formElements.map((element, index) => {
                              return (
                                <FormFields
                                  element={element}
                                  key={index}
                                  formik={formik}
                                  readOnly={readOnly}
                                  signature={signature}
                                />
                              );
                            })}
                          </FormikProvider>
                        </div>
                      )
                    );
                  }
                })}
                {isLastGroup && hasSignatureField && (
                  <div style={{ marginTop: 16 }}>
                    {isSigned && signature?.signedBy ? (
                      <>
                        <Typography variant="body2">
                          <strong>{t('Signed By:')}</strong>{' '}
                          {signature.signedBy}
                        </Typography>
                        {signature.signedAt && (
                          <Typography variant="body2">
                            <strong>{t('Signed On:')}</strong>{' '}
                            {new Date(signature.signedAt).toLocaleString()}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 'bold' }}
                        >
                          {signatureFieldLabel}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary'
                          }}
                        >
                          —
                        </Typography>
                      </>
                    )}
                  </div>
                )}
              </Container>
            );
          }
        })}
    </div>
  );
}
